import { useDispatch, useSelector } from 'react-redux';
import SocketAPI from './config/socket/socket';
import { useMissedCallBadge } from './hooks/useMissedCallBadge';
import React, { useEffect, useMemo, useRef } from 'react';
import { Storage, Utils } from './config';
import { DARK_MODE_EVENT, ROUTES } from './config/constants';
import { useLocation, useNavigate } from 'react-router';
import AuthRoutes from './config/routes/AuthRoutes';
// import { onMessage } from "firebase/messaging";
import { onMessage } from 'firebase/messaging';
import { registerDeviceForPush, messaging } from './config/firebase/firebase';
import notification_sound from './notification_sound.mp3';
import branch from 'branch-sdk';
import { Toaster } from './components';
import './App.css';
import { START_CALL_SUCCESS } from './store/constants';
import { MsgAction } from './store/actions';

/**
 * Push types that mean a call is over, whichever way it ended.
 *
 * Receiving one of these CLEARS `start_call`, which is what dismisses the call
 * screen - it watches for the call going away rather than subscribing to push
 * itself, so there is exactly one push handler in the app.
 *
 * `ENDED` is here because the backend now emits it when somebody hangs up. Before
 * that, a 1:1 call only dismissed on the peer because Agora happened to report
 * `user-left`, and a group call did not dismiss at all.
 */
/**
 * `HANDLED` belongs here because it IS terminal for whoever receives it: the call
 * was answered or refused on another of this user's devices, so this one stops
 * ringing. It is never sent to the other party — see `cancelRingElsewhere`.
 */
const TERMINAL_CALL_TYPES = new Set(['DECLINED', 'MISSED', 'ENDED', 'HANDLED']);

/**
 * A participant's page went away and may come back — a refresh, or a closed tab.
 *
 * Pointedly NOT in the set above. The call is still alive, so `start_call` must
 * survive: the screen stays up and the empty-channel timer in `useCallSession`
 * decides how long to wait. This type exists only to explain why the picture
 * stopped moving.
 */
const PEER_LEFT_TYPE = 'LEFT';

/** Answered or declined on another device of the same user. */
const PEER_HANDLED_TYPE = 'HANDLED';

/**
 * The id a call payload is known by, for comparison.
 *
 * A caller holds the create response, which names it `id`; a callee holds the
 * push, which names it `call_id`. Compared as strings because the push
 * stringifies everything. Deliberately NOT the same rule as `callIdOf` in
 * `useCallSession` — that one picks the id the API is addressed BY, which depends
 * on which side of the call you are; this one only has to tell two calls apart.
 */
const pushCallId = (call) => String(call?.call_id ?? call?.id ?? '');

const App = () => {
  const navigate = useNavigate();
  const socket = SocketAPI();

  /**
   * The cached user, read through Storage.
   *
   * This was a bare `JSON.parse(localStorage.getItem('user'))` in the render
   * body. Two problems, both of which this fixes:
   *
   * - `JSON.parse` throws on a malformed entry, and this is the ROOT component
   *   — a half-written value from an interrupted login took the whole app down
   *   to a blank page, with no boundary above it to catch anything.
   * - It produced a new object on every single render, so every effect keyed on
   *   it re-ran on every render. The deep-link effect below is the one that
   *   mattered: it re-resolved the Branch link continuously, which could
   *   re-navigate into a group invite the user had already left.
   *
   * Only the id is used here, so that is all that is held — a primitive is a
   * stable dependency where the object never was.
   */
  const userId = useMemo(() => Storage.getCachedUser()?.id ?? null, []);

  const ringtoneRef = useRef(null);
  const dispatch = useDispatch();
  const { refresh: refreshMissedCalls } = useMissedCallBadge(socket);
  // Read from the push handler without making it a dependency of that effect: the
  // handler re-subscribes to FCM when it re-runs, and `refresh` changes identity
  // whenever the socket does.
  const refreshMissedCallsRef = useRef(refreshMissedCalls);
  refreshMissedCallsRef.current = refreshMissedCalls;

  /**
   * The conversation currently ON SCREEN, or null.
   *
   * Both the direct and the group pane read their channel from this one store
   * key (`selected_chat_user.id` — see MessagesChat/GroupMessages), so one
   * selector covers both. Deliberately narrowed to the id: App is the root, and
   * selecting the conversation OBJECT would re-render the whole tree every time
   * the store refreshed it.
   *
   * The route check is not redundant. Closing a conversation only navigates
   * (`CloseItem` → /messages) and leaves the selection in the store, so the id
   * alone would keep suppressing that channel's notifications while the reader
   * sat on the calls list.
   *
   * Mirrored into a ref so the push handler below can read it without taking it
   * as a dependency — that effect resubscribes to Firebase when it re-runs, and
   * doing that on every navigation would drop pushes arriving in the gap.
   */
  const location = useLocation();
  /**
   * Read for one reason: to recognise a "handled on another device" push that this
   * device is itself the cause of. See the `PEER_HANDLED_TYPE` checks below.
   *
   * THROUGH A REF, not the value. Both push handlers below are deliberately
   * `[dispatch, navigate]`-deps — re-subscribing FCM's `onMessage` every time a
   * call changed state risks dropping a push during the swap — so anything they
   * read from the store is frozen at the first render unless it comes from a ref.
   * Read directly, this guard would have been permanently false.
   */
  const startCall = useSelector((state) => state?.MsgReducer?.start_call);
  const startCallRef = useRef(startCall);
  startCallRef.current = startCall;

  const selectedChannelId = useSelector(
    (state) => state?.MsgReducer?.selected_chat_user?.id ?? null
  );
  const openChannelId = /^\/messages\/.+/.test(location.pathname) ? selectedChannelId : null;
  const openChannelIdRef = useRef(null);
  openChannelIdRef.current = openChannelId;

  useEffect(() => {
    /**
     * Theme class. Light is the default. Both classes are written explicitly
     * rather than toggling `dark` alone: Tailwind's `dark:` variants and the
     * legacy `.dark …` selectors in bridge.css key off `.dark`, and tokens.css
     * declares `.light` as a reset so the light scale stays recoverable inside
     * a dark subtree. The boot script in public/index.html has already applied
     * this before first paint; this keeps it in step with the rail's switch.
     *
     * Reads go through Storage so a corrupt entry cannot throw — the raw
     * `JSON.parse` here used to take the whole shell down with it.
     */
    const applyTheme = () => {
      const isDarkMode = Storage.isDarkModeStored();
      const el = document.documentElement;
      el.classList.toggle('dark', isDarkMode);
      el.classList.toggle('light', !isDarkMode);
      el.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');

      // Keeps the browser chrome in step with the shell. Mirrors the boot
      // script; values track --bg in tokens.css.
      const themeColor = document.querySelector('meta[name="theme-color"]');
      if (themeColor) themeColor.setAttribute('content', isDarkMode ? '#0d0f14' : '#f5f6fa');
    };

    applyTheme();
    window.addEventListener(DARK_MODE_EVENT, applyTheme);
    window.addEventListener('storage', applyTheme);
    return () => {
      window.removeEventListener(DARK_MODE_EVENT, applyTheme);
      window.removeEventListener('storage', applyTheme);
    };
  }, []);

  useEffect(() => {
    registerDeviceForPush();

    /**
     * Connectivity, on one shared key so reconnecting REPLACES the disconnect
     * notice instead of leaving both on screen. This ran through a second,
     * separate antd `message` instance (`useMessage` + its own contextHolder),
     * which is why the two connectivity toasts used to appear in a different
     * place, and with a different stack, from every other toast in the app.
     *
     * The offline notice is pinned (`duration: 0`) — it stays true until the
     * connection is back, and the reconnect toast is what clears it.
     */
    const handleOnline = () =>
      Utils.showMessage('success', 'Back online.', { key: 'net-status', duration: 2.5 });
    const handleOffline = () =>
      Utils.showMessage('error', 'No internet connection.', { key: 'net-status', duration: 0 });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (!navigator.onLine) {
      handleOffline();
    }

    // Cleanup listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Background push relayed from the service worker.
   *
   * Guarded: `navigator.serviceWorker` is absent on non-secure origins, in some
   * private-browsing modes and on older browsers, and reading it unconditionally
   * threw while mounting the root component — a blank app. Named handler with a
   * teardown, so a remount cannot leave a second copy attached.
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onServiceWorkerMessage = (event) => {
      dispatch(MsgAction.UnreadMessageDot());

      if (event.data?.type !== 'BACKGROUND_MESSAGE') return;
      const notification = event.data.payload?.data;
      if (!notification) return;

      // Same routing as the foreground handler above, and for the same reasons —
      // a call is identified by the fields that make it joinable, and a terminal
      // outcome CLEARS the call rather than setting one up.
      if (TERMINAL_CALL_TYPES.has(notification.type)) {
        // Same guard as the foreground handler — see there.
        if (notification.type === PEER_HANDLED_TYPE && startCallRef.current?.accepted) return;

        dispatch({ type: START_CALL_SUCCESS, payload: null });
        return;
      }

      // The service worker has already shown this one as a system notification,
      // and the tab is not focused or this handler would not be the one running.
      // Toasting it as well would say the same thing twice.
      if (notification.type === PEER_LEFT_TYPE) return;

      if (
        notification.title === 'Calling..' &&
        notification.call_id &&
        notification.channel_id &&
        notification.token
      ) {
        // Same guard as the foreground handler, and for the same reasons — see
        // there. No toast: the service worker has already shown this one as a
        // system notification, and this handler only runs when the tab is not the
        // focused one.
        if (startCallRef.current) return;

        dispatch({
          type: START_CALL_SUCCESS,
          payload: { ...notification, isInComing: true },
        });
        navigate(ROUTES.CALL_ROOM(notification.call_id));
      }
    };

    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
  }, [dispatch, navigate]);

  /**
   * Foreground push — the ONE place a push is turned into app state.
   *
   * This used to treat every push that was not a reaction as an incoming call: it
   * played the tone, wrote `{...data, isInComing: true}` over `start_call` and
   * toasted the body as a success. So a "Call Declined" notice pushed the app INTO
   * incoming-call state, and an ordinary new-message push did the same — the call
   * screen would then read a `start_call` assembled from a message payload.
   *
   * The backend's payloads are distinct enough to route on properly
   * (chat.service.js):
   *   • incoming call — `title: 'Calling..'` plus `call_id` / `channel_id` / `token`,
   *     and a `type` of VIDEO or AUDIO (the call TYPE, not a status)
   *   • call outcome  — `type: 'DECLINED' | 'MISSED'`
   *   • everything else (messages, threads, group adds) — `{ title, body }` only
   *
   * So a call is identified by the fields it needs to actually be joinable, not by
   * the absence of some other marker.
   */
  useEffect(() => {
    if (!messaging) return undefined;

    const unsubscribe = onMessage(messaging, (payload) => {
      const data = payload?.data;
      if (!data) return;

      const ring = () => {
        if (!ringtoneRef.current) return;
        ringtoneRef.current.currentTime = 0;
        ringtoneRef.current.play().catch(() => {});
      };

      // ── the call is over ────────────────────────────────────────────────────
      // Clearing `start_call` is what dismisses the call screen: it watches for the
      // call going away rather than subscribing to push itself, so there is exactly
      // one push handler in the app. Never rings, never navigates to /calling.
      if (TERMINAL_CALL_TYPES.has(data.type)) {
        // Our own doing: this device answered, and the notice meant for its
        // siblings came back to it because the server could not identify it.
        // Acting on it would hang up the call we just joined.
        if (data.type === PEER_HANDLED_TYPE && startCallRef.current?.accepted) return;

        dispatch({ type: START_CALL_SUCCESS, payload: null });
        Utils.showMessage('info', data.body);
        // A call that ended without being answered is a new missed call. Re-read the
        // count rather than incrementing: only some of these outcomes are missed calls
        // for THIS user (the caller gets the same notice), and the server already knows
        // which. Without it the badge only ever appeared after a reload.
        refreshMissedCallsRef.current();
        return;
      }

      // ── somebody's page went away, but the call is still alive ─────────────
      if (data.type === PEER_LEFT_TYPE) {
        Utils.showMessage('info', data.body);
        return;
      }

      // ── somebody is calling ─────────────────────────────────────────────────
      const isIncomingCall =
        data.title === 'Calling..' && data.call_id && data.channel_id && data.token;

      if (isIncomingCall) {
        /**
         * ALREADY ON A CALL? LEAVE `start_call` ALONE.
         *
         * It is the single key `useJoin` reads, so overwriting it re-targets the
         * media channel: the call in progress loses its media, is never moved to a
         * terminal state — so its peer sits on a frozen picture until their own
         * empty-channel watchdog expires — and because a call was already present
         * the "call went away" watcher never runs, leaving `connected` and
         * `connectedAt` from the OLD call in place. The new call then rendered as
         * already answered, with the previous call's clock still running, and the
         * callee was never offered Accept.
         *
         * The same-id case is a duplicate delivery — FCM is at-least-once, and the
         * foreground handler and the service-worker relay can both fire. Re-
         * dispatching there would overwrite `accepted`, which is the flag the
         * HANDLED guard depends on, and hang up the call we are sitting in.
         *
         * A different id is a second caller, and it is IGNORED rather than
         * declined. That is a decision, not laziness: a DECLINED PATCH would be
         * filed as this USER's refusal and would call the ring off on their other
         * devices, but only this DEVICE is busy — a phone signed into the same
         * account should still be able to take the call. So the ring is left alone
         * and the caller's own no-answer watchdog files it as MISSED, which is
         * also what puts it in this user's missed-call history. Real call waiting
         * is the proper answer and a much larger piece of work; this is the part
         * that stops losing the call already in progress.
         */
        const active = startCallRef.current;
        if (active) {
          if (pushCallId(data) !== pushCallId(active)) {
            Utils.showMessage('info', 'Missed an incoming call — you were already on a call.');
          }
          return;
        }

        ring();
        dispatch({ type: START_CALL_SUCCESS, payload: { ...data, isInComing: true } });
        // Addressed by id, so this tab can find the call again after a reload without
        // depending on anything it holds in memory or in storage.
        navigate(ROUTES.CALL_ROOM(data.call_id));
        return;
      }

      // ── an ordinary notification ────────────────────────────────────────────
      // A reaction is not worth interrupting for at all.
      if (data.type === 'REACTION' || data.title === 'Message Reacted') return;

      /**
       * Already reading this conversation? Then this push is a second delivery of
       * something the reader is watching arrive: the socket appends the bubble to
       * the open stream at the same moment FCM toasts "you have 1 new message
       * from …" over the top of it.
       *
       * Suppressed on the client, so the push is still SENT — the reader's phone
       * and any other signed-in device still get it, which is the desired
       * behaviour for reading on one screen and being away from another. Only
       * this tab, which is demonstrably showing the message, stays quiet.
       *
       * The visibility check matters because `onMessage` also fires for a
       * foreground-but-hidden tab (a background tab in the same window, where
       * the service worker's background handler does not run). Nobody is
       * looking at that stream, so the notification is doing its job there.
       *
       * Guarded on `channel_id` being present at all: older backends and any
       * payload without one fall through to the toast rather than being silently
       * swallowed.
       */
      if (
        data.channel_id &&
        String(data.channel_id) === String(openChannelIdRef.current) &&
        document.visibilityState === 'visible'
      ) {
        return;
      }

      ring();
      // `info`, not `success`: nothing succeeded, something arrived.
      Utils.showMessage('info', data.body);
    });

    return unsubscribe;
  }, [dispatch, navigate]);
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onNavigateMessage = (event) => {
      if (event.data?.action === 'navigate') {
        const { url } = event.data;
        window.location.href = url; // Navigate to the URL sent by the service worker
      }
    };

    navigator.serviceWorker.addEventListener('message', onNavigateMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onNavigateMessage);
  }, []);

  /**
   * Seed the missed-call badge, and re-seed it after a reconnect.
   *
   * This ran once per mount and never again, so a socket that was not yet up — which is
   * the common case at boot, since the handshake and this effect race — left the badge
   * at zero for the whole session.
   */
  useEffect(() => {
    if (!socket || !userId) return undefined;

    refreshMissedCalls();
    socket.on('connect', refreshMissedCalls);
    return () => socket.off('connect', refreshMissedCalls);
  }, [socket, userId, refreshMissedCalls]);

  // Group invite opened from a Branch deep link. Resolved once per signed-in
  // session — see the note on `userId` for why this used to re-run forever.
  useEffect(() => {
    if (!userId) return;

    branch.data((err, data) => {
      if (err) return;

      const link = data?.data_parsed;
      if (!link?.group_id) return;

      navigate(`/join-groups/id=${link.group_id}/${link.group_name}/${link.isSecret}`);
    });
    branch.first(() => {});
  }, [userId, navigate]);

  return (
    // The AgoraRTCProvider that used to wrap this tree now lives in the lazily-loaded
    // calling route — see containers/Calling/CallRoute. Holding it here made the
    // 1.55 MB RTC SDK a static import of the ROOT component, so every session paid for
    // it at boot whether or not a call ever happened.
    <>
      <audio ref={ringtoneRef} src={notification_sound} preload="none" />
      <AuthRoutes />
      {/* The app's only toast surface. `react-toastify`'s ToastContainer used to
          sit here as well, with no `toast()` call anywhere to feed it — it
          rendered nothing and pulled in its own stylesheet. */}
      <Toaster />

      {/* <ComponentsSample /> */}
    </>
  );
};

export default App;
